import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import adminApiService from '../../services/adminApiService';
import { toast } from 'react-toastify';

const AdminTokenRechargeOperation = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [agentDetails, setAgentDetails] = useState(null);
  const [selectedTokens, setSelectedTokens] = useState('100');
  const [calculatedPrice, setCalculatedPrice] = useState('200');

  const tokenOptions = [
    { value: '100', label: '100 Tokens', price: 200 },
    { value: '200', label: '200 Tokens', price: 400 },
    { value: '300', label: '300 Tokens', price: 600 },
    { value: '400', label: '400 Tokens', price: 800 },
    { value: '500', label: '500 Tokens', price: 1000 },
    { value: '1000', label: '1000 Tokens', price: 2000 },
  ];

  useEffect(() => {
    if (agentId) {
      fetchAgentDetails();
    }
  }, [agentId]);

  useEffect(() => {
    const tokens = parseInt(selectedTokens);
    const price = tokens * 2;
    setCalculatedPrice(price.toString());
  }, [selectedTokens]);

  const fetchAgentDetails = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getAgentDetails(agentId);
      if (response.success) {
        setAgentDetails(response.data);
      } else {
        toast.error(response.message || 'Failed to fetch agent details');
      }
    } catch (error) {
      console.error('Error fetching agent details:', error);
      toast.error('Failed to fetch agent details');
    } finally {
      setLoading(false);
    }
  };

  const handleTokenChange = (e) => {
    setSelectedTokens(e.target.value);
  };

  const handlePayNow = async (e) => {
    e.preventDefault();
    
    try {
      setProcessing(true);
      const rechargeData = {
        tokens: selectedTokens,
        amount: calculatedPrice
      };

      const response = await adminApiService.processTokenRecharge(agentId, rechargeData);
      
      if (response.success) {
        toast.success(`Successfully recharged ${selectedTokens} tokens for ₹${calculatedPrice}!`);
        navigate('/admin/dashboard/agent-token-recharge');
      } else {
        toast.error(response.message || 'Failed to process token recharge');
      }
    } catch (error) {
      console.error('Error processing token recharge:', error);
      toast.error('Failed to process token recharge');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!agentDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Agent Not Found</h2>
          <p className="text-gray-600 mb-4">The agent with ID "{agentId}" could not be found.</p>
          <Link 
            to="/admin/dashboard/agent-token-recharge"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            Back to Token Recharge
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Token Recharge</h1>
              <p className="text-gray-600 mt-2">Process token recharge for agent</p>
            </div>
            <Link 
              to="/admin/dashboard/agent-token-recharge" 
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              ← Back to Token Recharge
            </Link>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm font-medium text-blue-800">Agent ID</div>
              <div className="text-blue-900 font-semibold">{agentDetails.agentId}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-blue-800">Agent Name</div>
              <div className="text-blue-900 font-semibold">
                {agentDetails.firstName} {agentDetails.lastName}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-blue-800">Email</div>
              <div className="text-blue-900">{agentDetails.email}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-blue-800">Current Tokens</div>
              <div className="text-blue-900 font-bold text-lg">{agentDetails.tokens || 0}</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            <div className="lg:w-2/5 bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center p-8">
              <div className="text-center text-white">
                <div className="w-32 h-32 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold mb-2">Token Recharge</h2>
                <p className="text-white text-opacity-90">Recharge tokens for your agent account</p>
              </div>
            </div>

            <div className="lg:w-3/5 p-8">
              <form onSubmit={handlePayNow} className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                    Select Token Package
                  </h3>
                </div>

                <div>
                  <label htmlFor="tokens" className="block text-sm font-medium text-gray-700 mb-2">
                    Select No. of Tokens
                  </label>
                  <select
                    id="tokens"
                    name="tokens"
                    value={selectedTokens}
                    onChange={handleTokenChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-lg"
                    required
                  >
                    {tokenOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label} - ₹{option.price}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
                    Total Price
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="price"
                      value={`₹${calculatedPrice}`}
                      readOnly
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-lg font-semibold text-orange-600"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <span className="text-gray-400 text-sm">INR</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Rate: ₹2 per token</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Recharge Summary</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Current Tokens:</span>
                      <span className="font-medium">{agentDetails.tokens || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tokens to Add:</span>
                      <span className="font-medium text-green-600">+{selectedTokens}</span>
                    </div>
                    <div className="flex justify-between border-t pt-1">
                      <span className="text-gray-900 font-medium">New Balance:</span>
                      <span className="font-bold text-blue-600">
                        {(parseInt(agentDetails.tokens || 0) + parseInt(selectedTokens))}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={processing}
                  className={`w-full py-4 px-6 rounded-lg text-white font-semibold text-lg transition-all duration-200 ${
                    processing
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 transform hover:scale-105 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {processing ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing Payment...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Pay Now - ₹{calculatedPrice}
                    </div>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTokenRechargeOperation;